<template>
  <div class="concern-trend">
    <div ref="plot-container">
      <svg>

        <!-- low/medium/high regions -->
        <g>
          <rect
            class="region"
            x="-1" y="0%" width="100%" height="23%"
          />
          <rect
            class="region"
            x="-1" y="69%" width="100%" height="31%"
          />
          <text class="fs-sm c-medium-gray" x="12" y="46" transform="rotate(-90,12,46)" text-anchor="middle" dominant-baseline="central">high</text>
          <text class="fs-sm c-medium-gray" x="12" y="185" transform="rotate(-90,12,185)" text-anchor="middle" dominant-baseline="central">medium</text>
          <text class="fs-sm c-medium-gray" x="12" y="335" transform="rotate(-90,12,335)" text-anchor="middle" dominant-baseline="central">low</text>
        </g>

        <!-- time axis -->
        <g class="time-axis">

          <line
            v-for = "tick in ticks"
            :key  = "`time-axis-line-${ tick.x }`"

            :x1 = "tick.x"
            :x2 = "tick.x"
            :y1 = "tickY1"
             y2 = 0
          />

          <text
            v-for = "tick in ticks"
            :key  = "`time-axis-text-${ tick.x }`"

            :x = "tick.x"
            :y = "tickLabelY"
            
            :style = "`font-size:${ TICK_LABEL_HEIGHT };`"
          >
            {{tick.label}}
          </text>

        </g>

        <!-- spline -->
        <g v-if="concernIsNotZero">
          <path
            class = "spline"
            :d    = "spline"
          />
        </g>

        <!-- markers -->
        <g v-if="concernIsNotZero">
          <circle
            v-for  = "( p, i ) in points"
            :key   = "i"
            :class = "p.color"
            :cx    = "p.x"
            :cy    = "p.y"
            r="5px"
          />
        </g>

      </svg>

      <div class = "flex flex-end mt-1">
        <div class="py-1_2 fs-sm">Trend over last:</div>
        <div
          class  = "plot-hours-selector b ba bc-medium-gray px-1 py-1_2 ml-1_2 fs-sm "
          v-for  = "h in plotHoursOptions"
          :key   = "h"
          :class = "{ 'active': ( plotHours === h ) }"
          @click = "plotHours = h; logTimeWindowChanged(h);"
        >
          {{h}}h
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { scaleTime, scaleLinear, line, curveMonotoneX } from 'd3';
import { doLogging }  from '../modules/log';

export default {

  name: 'ConcernTrend',

  props: {
    // concernScores: { type: Array, required: true },
    concernValuesWithColors: { type: Array, required: true },
  },

  data () {
    return {
      plotHours: 12,
      plotWidth: 400,
      plotHeight: 400,
      bottomPadding: 32,
      plotHoursOptions: [ 12, 24, 48, 72 ],
      PIXELS_BETWEEN_TICKS: 100,
      TICK_LABEL_HEIGHT: 12,
      TICK_LABEL_PADDING: 4,
    };
  },

  computed: {

    ...mapGetters([
      'currentDtm',
      'loggingContext',
      'concernIsZero',
    ]),

    concernIsNotZero () {
      return !( this.concernIsZero );
    },

    startDtm () {
      return this.dateAdd(
        this.currentDtm,
        -1 * this.plotHours,
        'hh'
      );
    },

    points () {
      return this.concernValuesWithColors.map( v => ({
        ...v,
        x: this.x( v.dtm ),
        y: this.y( v.value ),
      }));
    },

    spline () {
      return line()
        .x( p => this.x( p.dtm ) )
        .y( p => this.y( p.value ) )
        .curve( curveMonotoneX )( this.points );
    },

    x () {
      return scaleTime()
        .domain( [ this.startDtm, this.currentDtm ] )
        .range( [ 0, this.plotWidth ] );
    },  

    y () {
      return scaleLinear()
        //  for now, auto-scale to fit min/max values
        .domain( [0, 10] )
        .range( [ this.plotHeight - this.bottomPadding , 0 ] )
        .nice();
    },


    ticks () {

      const numTicks = Math.ceil( this.plotWidth / this.PIXELS_BETWEEN_TICKS );

      return this.x.ticks( numTicks )
        .map( t => ({
          x: this.x( t ),
          label: this.jsDateToHHMM( t ),
        }));
    },

    tickLabelY () {
      return this.plotHeight - this.TICK_LABEL_PADDING;
    },

    //  end tick line above label
    tickY1 () {
      return this.plotHeight - this.TICK_LABEL_HEIGHT - ( 2 * this.TICK_LABEL_PADDING );
    },
  },

  methods: {
    
    logTimeWindowChanged ( h ) {
      doLogging({
        ...this.loggingContext,
        eventType: 'trend time window changed',
        eventDetails: `${h} hours`
      });
    },

    jsDateToHHMM ( d ) {

      const hh = d.getHours().toString().padStart( 2, '0' ),
            mm = d.getMinutes().toString().padStart( 2, '0' );

      return `${ hh }:${ mm }`;
    },

    isoDatetoJS ( isoDtm ) {

      const year = parseInt( isoDtm.substring( 0, 4 ), 10 );
      const month = parseInt( isoDtm.substring( 5, 7 ), 10 ) - 1;
      const date = parseInt( isoDtm.substring( 8, 10 ), 10 );
      const hours = parseInt( isoDtm.substring( 11, 13 ), 10 );
      const minutes = parseInt( isoDtm.substring( 14, 16 ), 10 );
      const seconds = parseInt( isoDtm.substring( 17, 19 ), 10 );

      return new Date( year, month, date, hours, minutes, seconds );
    },

    dateAdd ( d, n, interval ) {

      const newDate = new Date( d );

      if ( interval === 'yy' ) {
        newDate.setFullYear( newDate.getFullYear() + n );

      } else if ( interval === 'mm' ) {
        newDate.setMonth( newDate.getMonth() + n );

      } else if ( interval === 'dd' ) {
        newDate.setDate( newDate.getDate() + n );

      } else if ( interval == 'hh' ) {
        newDate.setHours( newDate.getHours() + n );

      } else if ( interval == 'mi' ) {
        newDate.setMinutes( newDate.getMinutes() + n );

      } else if ( interval == 'ss' ) {
        newDate.setSeconds( newDate.getSeconds() + n );
      }

      return newDate;
    },

  }
};
</script>

<style lang="scss">
.concern-trend {
  width: 100%;
  /*height: 500px;*/
  position: relative;
}

svg {
  width: 100%;
  height: 400px;
  overflow: hidden;
}


.spline {
  fill: none;
  stroke-width: 5px;
  stroke: $medium-gray;
  transition: all 0.4s ease;
}

circle {
  r: 5px;
  stroke: transparent;
  fill:   $medium-gray;
  stroke-width: 10px;
  transition: all 0.4s ease;

  &:hover {
    r: 7px;
    stroke-width: 4px;
    fill: $almost-white;
  }

  &.red:hover    { stroke: $red; }
  &.yellow:hover { stroke: $yellow; }
  &.green:hover  { stroke: $green; }
}


rect.region {
  fill: white;
  stroke: $medium-gray;
  stroke-width: 1px;
}

.plot-hours-selector {
  user-select: none;
  cursor: pointer;
  padding-bottom: $gutter/2;

  &:hover {
    background-color: white;
    padding-bottom: $gutter/2 - 4;
    border-bottom-width:4px;
  }

  &.active {
    padding-bottom: $gutter/2 - 4;
    border-bottom-width:4px;
  }
}

.time-axis {

  line {
    stroke: $medium-gray;
    stroke-width: 1px;
  }

  text {
    text-anchor: middle;
  }
}
</style>
